/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    TextField,
    Grid,
    Paper,
    Stepper,
    Step,
    StepLabel,
    Divider,
    Alert,
    CircularProgress,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    Collapse,
    useMediaQuery,
    useTheme,
    Rating,
} from '@mui/material';
import {
    Hotel,
    Flight,
    DirectionsCar,
    Attractions,
    Person,
    Email,
    Phone,
    CalendarToday,
    LocationOn,
    CreditCard,
    Lock,
    CheckCircle,
    ArrowBack,
    ArrowForward,
    ExpandMore,
    ExpandLess,
    Celebration,
    Login,
    PersonAdd,
    Shield,
    Info,
} from '@mui/icons-material';
import { isAuthenticated } from '../utils/api';

// ============================================================================
// TYPES
// ============================================================================

interface TravelerInfo {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dateOfBirth: string;
    gender: string;
    isPrimary: boolean;
}

interface BookingDetails {
    type: 'hotel' | 'flight' | 'car' | 'activity';
    itemId: string;
    itemName: string;
    destination: string;
    checkIn: string;
    checkOut: string;
    rooms: number;
    guests: number;
    nights: number;
    pricePerNight: number;
    totalPrice: number;
}

// ============================================================================
// STEPS
// ============================================================================

const STEPS = [
    { label: 'Your Details', icon: <Person /> },
    { label: 'Review & Pay', icon: <CreditCard /> },
    { label: 'Confirmation', icon: <Celebration /> },
];

// ============================================================================
// HELPERS
// ============================================================================

const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const GuestCheckoutPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Check if user is authenticated
    const userIsAuthenticated = isAuthenticated();

    // Parse booking details from URL
    const bookingDetails: BookingDetails = {
        type: (searchParams.get('type') as 'hotel' | 'flight' | 'car' | 'activity') || 'hotel',
        itemId: searchParams.get('hotelId') || searchParams.get('flightId') || searchParams.get('itemId') || '',
        itemName: searchParams.get('hotelName') || searchParams.get('flightName') || searchParams.get('itemName') || '',
        destination: searchParams.get('destination') || '',
        checkIn: searchParams.get('checkIn') || '',
        checkOut: searchParams.get('checkOut') || '',
        rooms: parseInt(searchParams.get('rooms') || '1', 10),
        guests: parseInt(searchParams.get('guests') || '2', 10),
        nights: parseInt(searchParams.get('nights') || '1', 10),
        pricePerNight: parseFloat(searchParams.get('pricePerNight') || '0'),
        totalPrice: parseFloat(searchParams.get('totalPrice') || '0'),
    };

    // State
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Traveler details
    const [travelers, setTravelers] = useState<TravelerInfo[]>([
        {
            id: '1',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            dateOfBirth: '',
            gender: '',
            isPrimary: true,
        },
    ]);

    // Guest email (for non-authenticated users)
    const [guestEmail, setGuestEmail] = useState('');
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);

    // Login prompt dialog
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);

    // Payment state (placeholder)
    const [paymentProcessing, setPaymentProcessing] = useState(false);

    // Confirmation
    const [confirmationCode, setConfirmationCode] = useState('');

    // Order summary expanded
    const [summaryExpanded, setSummaryExpanded] = useState(!isMobile);

    // Add more travelers based on guest count
    useEffect(() => {
        if (bookingDetails.guests > travelers.length) {
            const newTravelers = [...travelers];
            for (let i = travelers.length; i < bookingDetails.guests; i++) {
                newTravelers.push({
                    id: (i + 1).toString(),
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    dateOfBirth: '',
                    gender: '',
                    isPrimary: false,
                });
            }
            setTravelers(newTravelers);
        }
    }, [bookingDetails.guests]);

    // Update traveler field
    const updateTraveler = (index: number, field: keyof TravelerInfo, value: string) => {
        const updated = [...travelers];
        updated[index] = { ...updated[index], [field]: value };
        setTravelers(updated);

        // Sync primary traveler email with guest email
        if (index === 0 && field === 'email') {
            setGuestEmail(value);
        }
    };

    // Validation
    const validateStep1 = () => {
        // Check primary traveler has required fields
        const primary = travelers[0];
        if (!primary.firstName || !primary.lastName) {
            setError('Please enter your name');
            return false;
        }
        if (!primary.email || !primary.email.includes('@')) {
            setError('Please enter a valid email address');
            return false;
        }
        if (!acceptTerms) {
            setError('Please accept the terms and conditions');
            return false;
        }
        setError('');
        return true;
    };

    // Handle continue to next step
    const handleContinue = () => {
        if (activeStep === 0) {
            if (!validateStep1()) return;

            // Show login prompt for guests
            if (!userIsAuthenticated) {
                setShowLoginPrompt(true);
                return;
            }
        }

        setActiveStep((prev) => prev + 1);
    };

    // Continue as guest
    const handleContinueAsGuest = () => {
        setShowLoginPrompt(false);
        setActiveStep(1);
    };

    // Redirect to login
    const handleLogin = () => {
        // Save booking state to localStorage
        localStorage.setItem('pendingBooking', JSON.stringify({
            bookingDetails,
            travelers,
            guestEmail,
        }));
        navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    };

    // Redirect to signup
    const handleSignup = () => {
        localStorage.setItem('pendingBooking', JSON.stringify({
            bookingDetails,
            travelers,
            guestEmail,
        }));
        navigate(`/signup?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    };

    // Complete booking
    const handleCompleteBooking = async () => {
        setPaymentProcessing(true);

        try {
            // Simulate payment processing
            await new Promise((resolve) => setTimeout(resolve, 2000));

            // Generate confirmation code
            const code = `PER-${Date.now().toString(36).toUpperCase()}`;
            setConfirmationCode(code);

            // TODO: Create trip and booking in backend
            // If guest, create guest_booking record
            // If authenticated, create trip with booking

            setActiveStep(2);
        } catch (err) {
            setError('Payment failed. Please try again.');
        } finally {
            setPaymentProcessing(false);
        }
    };

    // Calculate totals
    const subtotal = bookingDetails.totalPrice;
    const taxes = subtotal * 0.12;
    const total = subtotal + taxes;

    // Get item icon
    const getItemIcon = () => {
        switch (bookingDetails.type) {
            case 'hotel':
                return <Hotel sx={{ color: '#4A6FA5' }} />;
            case 'flight':
                return <Flight sx={{ color: '#2196F3' }} />;
            case 'car':
                return <DirectionsCar sx={{ color: '#607D8B' }} />;
            case 'activity':
                return <Attractions sx={{ color: '#2D6E6A' }} />;
            default:
                return <Hotel />;
        }
    };

    // =========================================================================
    // RENDER: STEP 1 - Traveler Details
    // =========================================================================

    const renderStep1 = () => (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person sx={{ color: '#2D6E6A' }} /> Guest Details
            </Typography>

            {travelers.map((traveler, index) => (
                <Card key={traveler.id} sx={{ mb: 2, borderRadius: 2 }}>
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                            {index === 0 ? 'Primary Guest (You)' : `Guest ${index + 1}`}
                            {index === 0 && (
                                <Chip label="Primary" size="small" sx={{ ml: 1, bgcolor: '#FFF3E0', color: '#E65100' }} />
                            )}
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    value={traveler.firstName}
                                    onChange={(e) => updateTraveler(index, 'firstName', e.target.value)}
                                    required={index === 0}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    value={traveler.lastName}
                                    onChange={(e) => updateTraveler(index, 'lastName', e.target.value)}
                                    required={index === 0}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            {index === 0 && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            type="email"
                                            value={traveler.email}
                                            onChange={(e) => updateTraveler(index, 'email', e.target.value)}
                                            required
                                            helperText="Confirmation will be sent here"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Email sx={{ color: '#999' }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Phone (optional)"
                                            type="tel"
                                            value={traveler.phone}
                                            onChange={(e) => updateTraveler(index, 'phone', e.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Phone sx={{ color: '#999' }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </CardContent>
                </Card>
            ))}

            {/* Terms and Newsletter */}
            <Box sx={{ mt: 3 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={acceptTerms}
                            onChange={(e) => setAcceptTerms(e.target.checked)}
                            sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                        />
                    }
                    label={
                        <Typography variant="body2">
                            I agree to the{' '}
                            <a href="/terms" target="_blank" style={{ color: '#2D6E6A' }}>
                                Terms & Conditions
                            </a>{' '}
                            and{' '}
                            <a href="/privacy" target="_blank" style={{ color: '#2D6E6A' }}>
                                Privacy Policy
                            </a>
                        </Typography>
                    }
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={subscribeNewsletter}
                            onChange={(e) => setSubscribeNewsletter(e.target.checked)}
                            sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                        />
                    }
                    label={
                        <Typography variant="body2">
                            Send me travel deals and inspiration (you can unsubscribe anytime)
                        </Typography>
                    }
                />
            </Box>
        </Box>
    );

    // =========================================================================
    // RENDER: STEP 2 - Review & Payment
    // =========================================================================

    const renderStep2 = () => (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCard sx={{ color: '#2D6E6A' }} /> Review & Payment
            </Typography>

            {/* Booking Summary */}
            <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        Booking Summary
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        {getItemIcon()}
                        <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {bookingDetails.itemName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {bookingDetails.destination}
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                                Check-in
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatDate(bookingDetails.checkIn)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                                Check-out
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatDate(bookingDetails.checkOut)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                                Duration
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {bookingDetails.nights} night{bookingDetails.nights > 1 ? 's' : ''}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                                Guests
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {bookingDetails.guests} guest{bookingDetails.guests > 1 ? 's' : ''}, {bookingDetails.rooms} room{bookingDetails.rooms > 1 ? 's' : ''}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* Travelers */}
                    <Typography variant="caption" color="text.secondary">
                        Guest{travelers.length > 1 ? 's' : ''}
                    </Typography>
                    {travelers.filter(t => t.firstName).map((t, i) => (
                        <Typography key={i} variant="body2">
                            {t.firstName} {t.lastName} {t.isPrimary && '(Primary)'}
                        </Typography>
                    ))}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Confirmation to: {travelers[0].email}
                    </Typography>
                </CardContent>
            </Card>

            {/* Price Breakdown */}
            <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        Price Details
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">
                            ${bookingDetails.pricePerNight} × {bookingDetails.nights} night{bookingDetails.nights > 1 ? 's' : ''}
                        </Typography>
                        <Typography variant="body2">${subtotal.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Taxes & fees
                        </Typography>
                        <Typography variant="body2">${taxes.toFixed(2)}</Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Total
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                            ${total.toFixed(2)}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            {/* Payment Method */}
            <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Lock sx={{ fontSize: 20 }} /> Payment Method
                    </Typography>

                    <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                        <Typography variant="body2">
                            <strong>Stripe Integration Coming Soon.</strong> Your payment will be processed securely.
                        </Typography>
                    </Alert>

                    {/* CC form placeholder */}
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Card Number"
                                placeholder="1234 5678 9012 3456"
                                disabled
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <CreditCard sx={{ color: '#ccc' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Expiry"
                                placeholder="MM / YY"
                                disabled
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="CVC"
                                placeholder="123"
                                disabled
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, color: 'text.secondary' }}>
                        <Shield sx={{ fontSize: 16 }} />
                        <Typography variant="caption">256-bit SSL encrypted · PCI DSS compliant</Typography>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );

    // =========================================================================
    // RENDER: STEP 3 - Confirmation
    // =========================================================================

    const renderStep3 = () => (
        <Box sx={{ textAlign: 'center', py: 4 }}>
            <Celebration sx={{ fontSize: 80, color: '#2D6E6A', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Booking Confirmed!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your trip to {bookingDetails.destination} is booked. 🎉
            </Typography>

            {/* Confirmation Code */}
            <Paper sx={{ p: 3, mb: 4, bgcolor: '#F5F5F5', borderRadius: 2, display: 'inline-block' }}>
                <Typography variant="caption" color="text.secondary">
                    Confirmation Code
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'monospace', color: '#2D6E6A' }}>
                    {confirmationCode}
                </Typography>
            </Paper>

            {/* Booking Details */}
            <Card sx={{ mb: 4, borderRadius: 2, textAlign: 'left', maxWidth: 500, mx: 'auto' }}>
                <CardContent>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        {getItemIcon()}
                        <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {bookingDetails.itemName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {formatDate(bookingDetails.checkIn)} – {formatDate(bookingDetails.checkOut)}
                            </Typography>
                        </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Confirmation sent to: <strong>{travelers[0].email}</strong>
                    </Typography>
                </CardContent>
            </Card>

            {/* Create Account CTA (for guests) */}
            {!userIsAuthenticated && (
                <Alert severity="info" sx={{ mb: 4, borderRadius: 2, maxWidth: 500, mx: 'auto', textAlign: 'left' }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Want to manage your booking?</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Create an account to access your itinerary, make changes, and earn credits on future bookings!
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<PersonAdd />}
                        onClick={handleSignup}
                        sx={{
                            bgcolor: '#2D6E6A',
                            textTransform: 'none',
                            borderRadius: 2,
                            '&:hover': { bgcolor: '#009688' },
                        }}
                    >
                        Create Account
                    </Button>
                </Alert>
            )}

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                {userIsAuthenticated ? (
                    <Button
                        variant="contained"
                        onClick={() => navigate('/trips')}
                        sx={{
                            bgcolor: '#2D6E6A',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 4,
                            '&:hover': { bgcolor: '#009688' },
                        }}
                    >
                        View My Trips
                    </Button>
                ) : (
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/')}
                        sx={{
                            borderColor: '#2D6E6A',
                            color: '#2D6E6A',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 4,
                        }}
                    >
                        Back to Home
                    </Button>
                )}
                <Button
                    variant="outlined"
                    onClick={() => navigate('/community')}
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                    }}
                >
                    Explore More Trips
                </Button>
            </Box>
        </Box>
    );

    // =========================================================================
    // MAIN RENDER
    // =========================================================================

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F5F5F5' }}>
            {/* Header */}
            <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', py: 2 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton onClick={() => navigate(-1)}>
                            <ArrowBack />
                        </IconButton>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                Complete Your Booking
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {bookingDetails.itemName}
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Stepper */}
            {activeStep < 2 && (
                <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', py: 2 }}>
                    <Container maxWidth="lg">
                        <Stepper activeStep={activeStep} alternativeLabel>
                            {STEPS.map((step, index) => (
                                <Step key={step.label} completed={index < activeStep}>
                                    <StepLabel
                                        StepIconProps={{
                                            sx: {
                                                '&.Mui-active': { color: '#2D6E6A' },
                                                '&.Mui-completed': { color: '#2D6E6A' },
                                            },
                                        }}
                                    >
                                        {step.label}
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Container>
                </Box>
            )}

            {/* Content */}
            <Container maxWidth="lg" sx={{ py: 3 }}>
                {activeStep === 2 ? (
                    renderStep3()
                ) : (
                    <Grid container spacing={3}>
                        {/* Main Content */}
                        <Grid item xs={12} md={8}>
                            {error && (
                                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            {activeStep === 0 && renderStep1()}
                            {activeStep === 1 && renderStep2()}

                            {/* Navigation */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ArrowBack />}
                                    onClick={() => (activeStep === 0 ? navigate(-1) : setActiveStep((prev) => prev - 1))}
                                    sx={{ borderRadius: 2, textTransform: 'none' }}
                                >
                                    Back
                                </Button>

                                {activeStep === 0 && (
                                    <Button
                                        variant="contained"
                                        endIcon={<ArrowForward />}
                                        onClick={handleContinue}
                                        sx={{
                                            bgcolor: '#2D6E6A',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            px: 4,
                                            '&:hover': { bgcolor: '#009688' },
                                        }}
                                    >
                                        Continue
                                    </Button>
                                )}

                                {activeStep === 1 && (
                                    <Button
                                        variant="contained"
                                        startIcon={paymentProcessing ? <CircularProgress size={20} color="inherit" /> : <Lock />}
                                        onClick={handleCompleteBooking}
                                        disabled={paymentProcessing}
                                        sx={{
                                            bgcolor: '#FF6B35',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            px: 4,
                                            '&:hover': { bgcolor: '#E55A2B' },
                                        }}
                                    >
                                        {paymentProcessing ? 'Processing...' : `Pay $${total.toFixed(2)}`}
                                    </Button>
                                )}
                            </Box>
                        </Grid>

                        {/* Order Summary Sidebar */}
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 2, borderRadius: 2, position: 'sticky', top: 100 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: isMobile ? 'pointer' : 'default',
                                    }}
                                    onClick={() => isMobile && setSummaryExpanded(!summaryExpanded)}
                                >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        Order Summary
                                    </Typography>
                                    {isMobile && (summaryExpanded ? <ExpandLess /> : <ExpandMore />)}
                                </Box>

                                <Collapse in={summaryExpanded}>
                                    <Box sx={{ mt: 2 }}>
                                        {/* Item */}
                                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                            {getItemIcon()}
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {bookingDetails.itemName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {bookingDetails.destination}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        {/* Dates */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <CalendarToday sx={{ fontSize: 16, color: '#666' }} />
                                            <Typography variant="body2">
                                                {formatDate(bookingDetails.checkIn)} – {formatDate(bookingDetails.checkOut)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Person sx={{ fontSize: 16, color: '#666' }} />
                                            <Typography variant="body2">
                                                {bookingDetails.guests} guest{bookingDetails.guests > 1 ? 's' : ''}, {bookingDetails.rooms} room{bookingDetails.rooms > 1 ? 's' : ''}
                                            </Typography>
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        {/* Price */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="body2">Subtotal</Typography>
                                            <Typography variant="body2">${subtotal.toFixed(2)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Taxes & fees
                                            </Typography>
                                            <Typography variant="body2">${taxes.toFixed(2)}</Typography>
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                Total
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                                                ${total.toFixed(2)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Collapse>

                                {/* Mobile collapsed price */}
                                {isMobile && !summaryExpanded && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Total
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                                            ${total.toFixed(2)}
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                )}
            </Container>

            {/* Login Prompt Dialog */}
            <Dialog
                open={showLoginPrompt}
                onClose={() => setShowLoginPrompt(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, textAlign: 'center', pt: 4 }}>
                    Save Your Booking
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', px: 4 }}>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        Create an account to manage your booking, earn credits, and access exclusive deals!
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                        <Button
                            variant="contained"
                            startIcon={<PersonAdd />}
                            onClick={handleSignup}
                            fullWidth
                            sx={{
                                bgcolor: '#2D6E6A',
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                py: 1.5,
                                '&:hover': { bgcolor: '#009688' },
                            }}
                        >
                            Create Account
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<Login />}
                            onClick={handleLogin}
                            fullWidth
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                py: 1.5,
                            }}
                        >
                            Log In
                        </Button>
                    </Box>

                    <Divider sx={{ my: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            or
                        </Typography>
                    </Divider>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 4 }}>
                    <Button
                        onClick={handleContinueAsGuest}
                        sx={{
                            textTransform: 'none',
                            color: 'text.secondary',
                        }}
                    >
                        Continue as Guest →
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GuestCheckoutPage;
